<script setup lang="ts">import { ref as _ref, computed as _computed, toRef as _toRef } from 'vue';

import { onClickOutside, useMagicKeys, watchDebounced } from '@vueuse/core'
import OmniSearchApi from '@consumer/api/OmniSearchApi'
import type { OmniSearchEmptyState, OmniSearchResponse } from '@consumer/types/serializers'
import { currentParams } from '@shared/url'
import { Combobox, ComboboxInput, ComboboxOptions } from '@headlessui/vue'
import { faArrowLeft, faMagnifyingGlass, faX } from '@fortawesome/pro-light-svg-icons'
import GTextInput from '@consumer/components/GTextInput.vue'
import { onKeyPress } from '@shared/composables/keyboard'
import { trackEvent } from '@shared/tracking'
import EmptyState from './OmniSearch/EmptyState.vue'
import LoadingSkeleton from './OmniSearch/LoadingSkeleton.vue'
import Results from './OmniSearch/Results.vue'
import ResultsWrapper from './OmniSearch/ResultsWrapper.vue'
import { useOmniSearch } from './OmniSearch/useOmniSearch'
import type { Result } from './OmniSearch/types'
import { detectHotWord, isGenericSearch, isRecentSearch } from './OmniSearch/utils'

defineProps<{
  scrolled?: boolean
}>()

const wrapperRef = ref<HTMLElement>()
const searchInput = _ref<any>()
const comboboxRef = _ref<any>()
const elastic = currentParams().get('elastic')
const cache = currentParams().get('cache')
const comboboxContext = _computed(() => {
  const contextKey = Object.getOwnPropertySymbols(comboboxRef.value.$.provides)[0]
  return comboboxRef.value.$.provides[contextKey]
})
let loading = _ref(false)

const __$temp_1 = (useOmniSearch({ setup: true })),
  searchResponse = _toRef(__$temp_1, 'searchResponse'),
  query = _toRef(__$temp_1, 'query'),
  trimmedQuery = _toRef(__$temp_1, 'trimmedQuery'),
  selectedResult = _toRef(__$temp_1, 'selectedResult'),
  setSearchResponse = _toRef(__$temp_1, 'setSearchResponse'),
  setEmptyState = _toRef(__$temp_1, 'setEmptyState'),
  reset = _toRef(__$temp_1, 'reset'),
  selectResult = _toRef(__$temp_1, 'selectResult'),
  setQuery = _toRef(__$temp_1, 'setQuery'),
  open = _toRef(__$temp_1, 'open'),
  setOpen = _toRef(__$temp_1, 'setOpen'),
  showAllResultsValue = _toRef(__$temp_1, 'showAllResultsValue');

onKeyPress(useMagicKeys().meta_k, () => searchInput.value?.el?.focus())

watch(
  () => trimmedQuery.value,
  () => { reset.value() },
)

watchDebounced(
  () => [trimmedQuery.value],
  async () => {
    if (!trimmedQuery.value?.length) {
      setSearchResponse.value(undefined)
      return
    }

    const hotWord = detectHotWord(trimmedQuery.value)
    if (hotWord) {
      setSearchResponse.value({
        query: trimmedQuery.value,
        results: [],
        searchSubject: trimmedQuery.value,
        suggestions: [],
        hotWord: !!hotWord,
      })
      return
    }

    let response: OmniSearchResponse

    try {
      const elasticQuery = elastic ? { elastic: elastic === 'true' } : {}
      const cacheQuery = cache ? { cache: cache === 'true' } : {}
      trackEvent('change', 'omni-search-query', trimmedQuery.value)
      response = await OmniSearchApi.index<OmniSearchResponse>({
        query: { query: trimmedQuery.value, ...elasticQuery, ...cacheQuery },
      })
    }
    catch (error) {
      console.error('Error fetching search results:', error)
      // If something fails, default to the Any Place state.
      setSearchResponse.value({
        query: trimmedQuery.value,
        genericSuggestion: 'Any Place',
        nearbySuggestion: 'Any place',
        results: [],
        searchSubject: trimmedQuery.value,
        suggestions: [],
      })
      return
    }

    if (trimmedQuery.value.toLocaleLowerCase() === response.query.toLocaleLowerCase())
      setSearchResponse.value(response)
  },
  { debounce: 300, maxWait: 1000 },
)

watch(
  () => open.value,
  (isOpen) => {
    if (isOpen) {
      trackEvent('click', 'omni-search-open')
    }
    else {
      trackEvent('click', 'omni-search-close')
      searchInput.value?.el?.blur()
    }
    changeComboboxInternalState(isOpen)
  },
)

watch(() => searchResponse.value, unsetActiveOption)

onMounted(async () => {
  const cacheQuery = cache ? { cache: cache === 'true' } : {}

  setEmptyState.value(
    await OmniSearchApi.emptyState<OmniSearchEmptyState>({ query: cacheQuery }),
  )

  // Prevent showing the loading indicator when the page is loaded coming
  // from a browser back/forward navigation.

  // Reset loading state when page becomes visible again (handles browser back/forward navigation)
  const handleVisibilityChange = () => {
    if (!document.hidden) {
      loading.value = false
    }
  }

  document.addEventListener('visibilitychange', handleVisibilityChange)

  // Cleanup event listener on unmount
  onBeforeUnmount(() => {
    document.removeEventListener('visibilitychange', handleVisibilityChange)
  })
})

// Additional safeguard for when component is reactivated (e.g., with KeepAlive)
onActivated(() => loading.value = false)

onClickOutside(wrapperRef, () => setOpen.value(false))

// HeadlessUI Combobox sets the first option as active by default.
// This method unsets that active option so we make the user hit the arrow
// key down to select the first option.
function unsetActiveOption () {
  comboboxContext.value.goToOption(5) // 5 = Nothing
}

// We need to do this so because we're managing the combobox state ourselves with the "open" ref.
// We need to "sync" this state with the combobox state so that the keyboard navigation works correctly.
function changeComboboxInternalState (open: boolean) {
  if (open) {
    comboboxContext.value.openCombobox()
    unsetActiveOption()
  }
  else {
    comboboxContext.value.closeCombobox()
  }
}

function onResultSelected (result?: Result) {
  if (result && !isRecentSearch(result)) {
    searchInput.value?.el?.blur()
    if (!isGenericSearch(result))
      loading.value = true
  }

  selectResult.value(result)

  // Keep the internal state of the combobox as open.
  setTimeout(() => changeComboboxInternalState(true), 100)
}

// When the enter key is pressed in the combobox and there's no activeOption set, we assume
// the user wants to show all results for the search term.
function onEnterPressed () {
  if (!selectedResult.value && comboboxContext.value.activeOptionIndex.value === null && trimmedQuery.value?.length) {
    onResultSelected(showAllResultsValue.value)
  }
}

// When the arrow down is pressed and there's no active option set we active index to be 0 due to a
// bug in headless ui: https://github.com/tailwindlabs/headlessui/issues/3775
function onArrowDownPressed () {
  if (comboboxContext.value.activeOptionIndex.value === null) {
    // 4 = Specific index, index 0
    // see https://github.com/tailwindlabs/headlessui/blob/main/packages/%40headlessui-vue/src/utils/calculate-active-index.ts#L19
    comboboxContext.value.goToOption(4, 0)
  }
  else {
    if (comboboxContext.value.activeOptionIndex.value === comboboxContext.value.options.value.length - 1) {
      comboboxContext.value.goToOption(0) // 0 = first option
    }
  }
}

// When the arrow up is pressed and there's no active option set we active index to be the last item
// in the combobox
function onArrowUpPressed () {
  if (comboboxContext.value.activeOptionIndex.value === null || comboboxContext.value.activeOptionIndex.value === 0) {
    // 3 = Last element
    // see https://github.com/tailwindlabs/headlessui/blob/main/packages/%40headlessui-vue/src/utils/calculate-active-index.ts#L16C3-L16C7
    comboboxContext.value.goToOption(3)
  }
}
</script>

<template>
  <!--
    The combobox has to be "multiple" due to some bugs the headless ui combobox library has.
    It's setting the active option as the selected option when blurring the input, which is not the desired behavior.
    See https://github.com/tailwindlabs/headlessui/issues/3771
  -->
  <Combobox
    ref="comboboxRef"
    :modelValue="selectedResult"
    class="relative w-full z-navbar"
    as="div"
    @update:modelValue="onResultSelected"
  >
    <!-- Overlay -->
    <Transition name="fade-in-out">
      <div
        v-show="open"
        class="fixed inset-0 bg-black <lg:bg-white opacity-30 <lg:opacity-100"
      />
    </Transition>

    <div
      ref="wrapperRef"
      class="omni-search-wrapper relative w-full"
      :class="{ '!<lg:-mt-12 <lg:transition-all' : !scrolled && open }"
    >
      <!-- Back arrow button, closes the search. Only on mobile -->
      <div
        class="
          lg:hidden
          absolute left-4.5 z-10
          flex items-center justify-center
          h-full
        "
      >
        <GIconButton
          v-if="open"
          :icon="faArrowLeft"
          size="small"
          class="close-search"
          @mousedown="setOpen(false)"
        />
        <div v-else class="p-3 text-v2-tertiary" @click="setOpen(true); searchInput.el?.focus()">
          <GIcon :icon="faMagnifyingGlass"/>
        </div>
      </div>

      <!-- Search input - determines the query -->
      <div class="<lg:px-3 <lg:py-2">
        <ComboboxInput
          ref="searchInput"
          v-model="query"
          name="query"
          :as="GTextInput"
          :displayValue="() => ''"
          placeholder="Search by place, brand, or interest..."
          class="w-full <lg:pl-11 lg:pr-14 transition-all"
          :class="{
            '<lg:pr-14': trimmedQuery?.length,
          }"
          autocomplete="off"
          enterkeyhint="search"
          @focus="setOpen(true)"
          @keydown.esc="setOpen(false)"
          @keydown.enter.prevent.stop="onEnterPressed"
          @keydown.down.prevent.stop="onArrowDownPressed"
          @keydown.up.prevent.stop="onArrowUpPressed"
          @blur.capture.stop
        />
      </div>

      <!-- Search Button (only desktop, opens up the search - same as focus) -->
      <GButton
        v-if="!loading"
        variant="primary"
        type="submit"
        :icon="faMagnifyingGlass"
        size="small"
        name="Search"
        class="!<lg:hidden aspect-square absolute right-1.5 top-1 z-10 rounded-v2md"
        style="margin-top: 1px;"
        @click.prevent.stop="searchInput?.el?.focus()"
      />

      <LoadingIndicator
        v-if="loading"
        class="absolute right-5 top-5 <lg:top-7 <lg:right-6 z-10"
        :style="{ marginTop: '-1px' }"
      />

      <!-- X button, clears the query. Only on mobile -->
      <div
        v-if="!loading"
        class="
          lg:hidden
          absolute right-6 top-0 z-10
          flex items-center justify-center
          h-full
          opacity-0 transition-opacity duration-300
          pointer-events-none
        "
        :class="{ '!opacity-100 pointer-events-auto': open && trimmedQuery?.length }"
      >
        <GIconButton
          :icon="faX"
          size="small"
          aria-label="Clear search"
          @mousedown.stop.prevent="setQuery('')"
        />
      </div>

      <!-- Search Results -->
      <Transition name="fade-in-out">
        <ComboboxOptions v-if="open" static as="div">
          <ResultsWrapper>
            <EmptyState v-if="!trimmedQuery?.length"/>
            <LoadingSkeleton v-else-if="!searchResponse"/>
            <Results v-else/>
          </ResultsWrapper>
        </ComboboxOptions>
      </Transition>
    </div>
  </Combobox>
</template>

<style scoped lang="scss">
:deep(.addon-right) {
  padding-right: 8px;
}
</style>
